/**
 * Island Gold · i18n dictionary.
 *
 * Keys are English source strings → object of locale translations.
 * `null` means "not translated yet — fall back to English".
 *
 * Chuukese strings marked `_seeded: "ai"` are AI-translated and awaiting
 * Ymaku's review (she is the only authoritative source). Edit in place
 * once she approves.
 */

export const SUPPORTED_LOCALES = [
    { code: "en", label: "English", short: "EN" },
    { code: "chk", label: "Chuukese", short: "CHK" },
];

export const DEFAULT_LOCALE = "en";

/**
 * Browser locale detection.
 * - navigator.language returns like "en-US", "chk", "en"
 * - If anything maps to our supported set, use it; else default.
 * - A manual override in localStorage ALWAYS wins.
 */
export function detectLocale() {
    try {
        const stored = localStorage.getItem("ig_locale");
        if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) {
            return stored;
        }
        const nav = (navigator.language || "en").toLowerCase();
        if (nav.startsWith("chk") || nav.startsWith("ckh")) return "chk";
        // Known FSM / Micronesian locale codes could be mapped here later.
        return "en";
    } catch {
        return "en";
    }
}

export function setLocale(code) {
    try {
        localStorage.setItem("ig_locale", code);
    } catch {
        /* ignore */
    }
}

/**
 * Translation table — grouped by surface for easier Ymaku review.
 * Keys are stable identifiers; values hold locale strings.
 */
export const DICT = {
    // ---------------- NAV ----------------
    "nav.inventory": { en: "Inventory", chk: "Pisekin Mettoch" },
    "nav.our_story": { en: "Our Story", chk: "Pwóróusen Ach" },
    "nav.our_roots": { en: "Our Roots", chk: "Pwúngún Ach" },
    "nav.the_craft": { en: "The Craft", chk: "Eweer Angaang" },
    "nav.partners": { en: "Partners", chk: "Chienach Chón Angaang" },
    "nav.cart": { en: "Collection", chk: "Finen Mettoch" },

    // ---------------- HERO (home) ----------------
    "hero.badge": {
        en: "Island Gold Glow · Hawaiʻi",
        chk: "Island Gold Glow · Hawaiʻi",
    },
    "hero.h1.l1": { en: "Handcrafted.", chk: "Féér ren pééún aramas." },
    "hero.h1.l2": { en: "Island-Born.", chk: "Mi uputá wóón ewe fénú." },
    "hero.h1.l3": { en: "Made to Glow.", chk: "Afféérún saramen." },
    "hero.subhead": {
        en: "Handmade soap from Hawaiʻi. First small batches coming soon.",
        chk: "Handmade soap from Hawaiʻi. First small batches coming soon.",
    },
    "hero.cta.shop": { en: "Shop Island Gold", chk: "Nínéé Island Gold" },
    "hero.cta.story": { en: "Our Story", chk: "Pwóróusen Ach" },

    // ---------------- OUR STORY ----------------
    "story.eyebrow": {
        en: "Island Gold Glow Soap Co.",
        chk: "Island Gold Glow Soap Co.",
    },
    "story.h1": { en: "Our Story.", chk: "Pwóróusen Ach." },
    "story.lede1": {
        en: "Island Gold didn't start in a lab.",
        chk: "Island Gold ese poputá me non eew lab.",
    },
    "story.lede2": {
        en: "It started with the ocean — and the people who've lived beside it for generations.",
        chk: "A poputá seni ewe sáát — me ekkewe aramas ra fen manaw ren fansoun chón mwáán.",
    },
    "story.body1_prefix": { en: "", chk: "" },
    "story.body1_suffix": {
        en: "grew up carrying the traditions of Chuuk, Guam, and Hawaii — three Pacific Island communities bound by the same deep water, the same ancestral plants, and the same way of caring for family through what the land provides.",
        chk: "a máárá me a uwa fáánún Chuuk, Guam, me Hawaii — úlúnúngát Island Pacific ra kapachelong non eew chék sáát, eew chék iréén chón mwáán, me eew chék napanapen tumwun famili ren met ewe fénú a awora.",
    },
    "story.body2": {
        en: "Turmeric root ground by hand. Coconut harvested fresh. Taro root. Island botanicals passed down, not formulated up.",
        chk: "Láwer turmeric a méúmééún pééún aramas. Núú a fatiw mi minafen. Láwer taro. Iréén fénú ra sóupwú tiwow, esap féér seni koror.",
    },
    "story.body3_prefix": {
        en: "These weren't beauty ingredients. They were medicine. Ceremony. The way her people said",
        chk: "Ekkeei esap mettochun lingen inis. Ir mi safei. Féffér mwáán. Ewe napanap ren an aramas ra apasa",
    },
    "story.body3_love": { en: "I love you", chk: "Ua tongei en" },
    "story.body3_suffix": {
        en: "through what they put on their skin.",
        chk: "ren met ra isetiw wóón únúcher.",
    },
    "story.pullquote": {
        en: "She wasn't launching a brand. She was continuing something older than any storefront.",
        chk: "Ese poputá eew brand. A amúúchúnúnú ewe mettoch mi watteen seni meinisin ánó.",
    },
    "story.body4_suffix": {
        en: "began making soap, we weren't launching a brand. We were continuing something older than any storefront — a living tradition of island craft, brought into our own hands.",
        chk: "a poputá féér soopun, ese poputá eew brand. A amúúchúnúnú ewe mettoch mi watteen seni meinisin ánó — eew fáánún féffér mi manaw seni ewe fénú, a uwá ngeni pééún.",
    },
    "story.body5": {
        en: "Island Gold Glow Soap Co. is what happens when that tradition meets today. Every bar is handcrafted with real island ingredients — not to chase a wellness trend, but because this is what her community has always known.",
        chk: "Island Gold Glow Soap Co. ina met a fis lupwen ewe fáánún a chuuri ikenai. Iteiten soop a féér ren pééún aramas me minafen mettochun fénú — esap pwe repwe fin fitiéti eew wellness trend, nge pwe ina met an aramas ra sinei seni mwáán.",
    },
    "story.body6": {
        en: "Pacific people were here before the market discovered coconut oil. They'll be here long after.",
        chk: "Aramasen Pacific ra fen nóm ikai me mwán ewe market a sineiti coconut oil. Repwe nóm ikai mwirin.",
    },
    "story.close.l1": {
        en: "This is their craft.",
        chk: "Ei ina angaanger.",
    },
    "story.close.l2": { en: "This is her story.", chk: "Ei ina pwóróusan." },
    "story.close.l3": {
        en: "This is Island Gold.",
        chk: "Ei ina Island Gold.",
    },
    "story.mahalo": {
        en: "Mahalo for being here.",
        chk: "Kinisou chapwúr ngenuk ren om ka nóm ikai.",
    },
    "story.cta.first_drop": {
        en: "See the First Drop",
        chk: "Nennengeni ewe Akkomw",
    },
    "story.cta.craft": {
        en: "How the Soap Is Made →",
        chk: "Ifa Usun ewe Soop a Féér →",
    },

    // ---------------- INVENTORY ----------------
    "inv.first_drop_badge": {
        en: "First Drop · Coming Soon",
        chk: "Akkomw · Epwe Arap",
    },
    "inv.h1.l1": { en: "Island Gold Glow", chk: "Island Gold Glow" },
    "inv.h1.l2": { en: "Collection", chk: "Finen Mettoch" },
    "inv.subhead": {
        en: "Handmade in Hawaiʻi. Volcanic minerals. Island botanicals. Every bar poured by hand and slow-cured for weeks before it earns the Gold mark. The first drop is almost ready — join the waitlist to be first in line.",
        chk: "Féér ren pééún aramas wóón Hawaiʻi. Minerals seni volcano. Iréén fénú. Iteiten soop a féér me monomón ren fansoun wiik mwán epwe tongeni ewe esisinen Gold. Ewe akkomw a arap — kopwe pacheta ewe waitlist pwe ar kopwe akomw.",
    },
    "inv.waitlist_label": {
        en: "Notify Me On Drop",
        chk: "Esinei Ngeniei lupwen a Tongor",
    },
    "inv.waitlist_submit": { en: "Notify", chk: "Esinei" },
    "inv.on_list": {
        en: "You're on the list. We'll email when it drops.",
        chk: "Iten en a nóm wóón ewe list. Áám áápwa esinei ngenuk lupwen a tongor.",
    },

    // ---------------- FOOTER ----------------
    "footer.waitlist_h": { en: "Join the Waitlist.", chk: "Pacheta ewe Waitlist." },
    "footer.waitlist_p": {
        en: "Handmade soap from Hawaiʻi. Small batches, island botanicals. We'll email you when the first bar is ready.",
        chk: "Handmade soap from Hawaiʻi. Small batches, island botanicals. We'll email you when the first bar is ready.",
    },
    "footer.email_placeholder": {
        en: "your@email.com",
        chk: "om@email.com",
    },
    "footer.subscribe": { en: "Subscribe →", chk: "Pacheta →" },

    // ---------------- CONCIERGE ----------------
    "concierge.open": { en: "Ask Island Gold", chk: "Eis Island Gold" },
    "concierge.welcome": {
        en: "Aloha. I'm the Island Gold Glow concierge — ask me anything about the soaps, the ingredients, or when the first bar drops. 🌿",
        chk: "Ran annim. Ngang ewe chón alillis me ren Island Gold Glow — kopwe eisiniei met óóch ren ekkena soop, met kokkot ir, are inet ewe akkomwun soop epwe tongor. 🌿",
    },
    "concierge.input_placeholder": {
        en: "Ask about the first drop…",
        chk: "Eis usun ewe akkomw…",
    },
};

export function t(key, locale) {
    const row = DICT[key];
    if (!row) return key; // dev-safety: show the key if missing
    return row[locale] || row.en || key;
}
